import { toast } from 'react-toastify';
import { all, call, fork, put, takeEvery } from 'redux-saga/effects';
import { ApiResponse } from 'api/ApiClient';
import { SuccessToastContainer } from 'components/Toast/ToastContainer';
import {
  ChannelType,
  IAccountRecovery,
  IAccountRecoveryResendCode,
  IAccountRecoveryVerification,
  IAction,
  ICreateAccountRecovery,
  ICreateAccountRecoveryIdentity,
  IIdentity,
  IVerifyAccountRecovery,
  IVerifyIdentity,
  IVerifyIdentityRequest,
} from 'types';
import { formatErrorMessages } from 'utils/errors';
import * as API from '../../api';
import * as Actions from './actions';
import * as ActionTypes from './types';

interface IAccountRecoveryPayloadProps {
  id: string;
  body: ICreateAccountRecovery | IVerifyAccountRecovery | ICreateAccountRecoveryIdentity | IVerifyIdentityRequest;
  onSuccess: () => void;
}

export function* createAccountRecovery({ payload }: IAction<ActionTypes.AccountRecovery, IAccountRecoveryPayloadProps>) {
  try {
    const body = payload.body as ICreateAccountRecovery;
    const response: ApiResponse<IAccountRecovery> = yield call(API.AccountRecovery.createAccountRecovery, payload.id, body);
    yield put(Actions.createAccountRecoverySuccess(response.body));

    // Execute on success function
    if (payload.onSuccess) yield call(payload.onSuccess);
  } catch (error: any) {
    const formattedError = formatErrorMessages({ errors: error.body?.errors, statusCode: error.statusCode, requestMethod: 'POST', requestName: 'createAccountRecovery' });
    yield put(Actions.createAccountRecoveryError(formattedError));
  }
}

export function* verifyEmail({ payload }: IAction<ActionTypes.AccountRecovery, IAccountRecoveryPayloadProps>) {
  try {
    const body = payload.body as IVerifyAccountRecovery;
    const response: ApiResponse<IAccountRecoveryVerification> = yield call(API.AccountRecovery.verifyAccountRecovery, payload.id, body);
    yield put(Actions.verifyEmailSuccess(response.body));

    // Execute on success function
    if (payload.onSuccess) yield call(payload.onSuccess);
  } catch (error: any) {
    const formattedError = formatErrorMessages({ errors: error.body?.errors, statusCode: error.statusCode, requestMethod: 'PUT', requestName: 'verifyAccountRecovery' });
    yield put(Actions.verifyEmailError(formattedError));
  }
}

export function* addIdentity({ payload }: IAction<ActionTypes.AccountRecovery, IAccountRecoveryPayloadProps>) {
  try {
    const body = payload.body as ICreateAccountRecoveryIdentity;
    const response: ApiResponse<IIdentity> = yield call(API.AccountRecovery.createIdentity, payload.id, body);
    yield put(Actions.addIdentitySuccess(response.body));

    // Execute on success function
    if (payload.onSuccess) yield call(payload.onSuccess);
  } catch (error: any) {
    const formattedError = formatErrorMessages({ errors: error.body?.errors, statusCode: error.statusCode, requestMethod: 'PUT', requestName: 'createIdentity' });
    yield put(Actions.addIdentityError(formattedError));
  }
}

interface IVerifyIdentityProps extends IAccountRecoveryPayloadProps {
  identityId: string;
}

export function* verifyIdentity({ payload }: IAction<ActionTypes.AccountRecovery, IVerifyIdentityProps>) {
  try {
    const body = payload.body as IVerifyIdentityRequest;
    const response: ApiResponse<IVerifyIdentity> = yield call(API.AccountRecovery.verifyIdentity, payload.id, payload.identityId, body);
    yield put(Actions.verifyIdentitySuccess(response.body));

    // Execute on success function
    if (payload.onSuccess) yield call(payload.onSuccess);

    yield put(Actions.accountRecoveryClear());
    yield call(toast, SuccessToastContainer({ message: 'Account recovery successfully completed!' }));
  } catch (error: any) {
    const formattedError = formatErrorMessages({ errors: error.body?.errors, statusCode: error.statusCode, requestMethod: 'PUT', requestName: 'verifyIdentity' });
    yield put(Actions.verifyIdentityError(formattedError));
  }
}

export function* accountRecoveryResendVerificationCode({ payload }: IAction<ActionTypes.AccountRecovery, { id: string }>) {
  try {
    const response: ApiResponse<IAccountRecoveryResendCode> = yield call(API.AccountRecovery.resendAccountRecoveryCode, payload.id);

    yield put(Actions.resendEmailVerificationCodeSuccess(response.body));
    yield call(toast, SuccessToastContainer({ message: 'Verification code resent successfully' }));
  } catch (error: any) {
    const formattedError = formatErrorMessages({ errors: error.body?.errors, statusCode: error.statusCode, requestMethod: 'PUT', requestName: 'accountRecoveryResendVerificationCode' });
    yield put(Actions.resendEmailVerificationCodeError(formattedError, error.statusCode >= 500));
  }
}

interface IResendIdentityVerificationCodeProps {
  id: string;
  identityId: string;
  channel?: ChannelType.CALL;
  successMessage?: string;
}

export function* accountRecoveryResendIdentityVerificationCode({ payload }: IAction<ActionTypes.AccountRecovery, IResendIdentityVerificationCodeProps>) {
  try {
    const response: ApiResponse<IIdentity> = yield call(API.AccountRecovery.resendVerificationCode, payload.id, payload.identityId, payload.channel);

    yield put(Actions.resendIdentityVerificationCodeSuccess(response.body));
    yield call(toast, SuccessToastContainer({ message: payload.successMessage || 'Verification code resent successfully' }));
  } catch (error: any) {
    const formattedError = formatErrorMessages({ errors: error.body?.errors, statusCode: error.statusCode, requestMethod: 'PUT', requestName: 'accountRecoveryResendIdentityVerificationCode' });
    yield put(Actions.resendIdentityVerificationCodeError(formattedError, error.statusCode >= 500));
  }
}

export function* watchCreateAccountRecovery() {
  yield takeEvery(ActionTypes.AccountRecovery.CREATE_ACCOUNT_RECOVERY_REQUEST, createAccountRecovery);
}

export function* watchAccountRecoveryVerifyEmail() {
  yield takeEvery(ActionTypes.AccountRecovery.EMAIL_VERIFY_REQUEST, verifyEmail);
}

export function* watchAccountRecoveryAddIdentity() {
  yield takeEvery(ActionTypes.AccountRecovery.ADD_IDENTITY_REQUEST, addIdentity);
}

export function* watchAccountRecoveryVerifyIdentity() {
  yield takeEvery(ActionTypes.AccountRecovery.VERIFY_IDENTITY_REQUEST, verifyIdentity);
}

export function* watchAccountRecoveryResendVerificationCode() {
  yield takeEvery(ActionTypes.AccountRecovery.RESEND_EMAIL_VERIFICATION_CODE_REQUEST, accountRecoveryResendVerificationCode);
}

export function* watchAccountRecoveryResendIdentityVerificationCode() {
  yield takeEvery(ActionTypes.AccountRecovery.RESEND_IDENTITY_VERIFICATION_CODE_REQUEST, accountRecoveryResendIdentityVerificationCode);
}

function* accountRecoverySaga() {
  yield all([
    fork(watchCreateAccountRecovery),
    fork(watchAccountRecoveryVerifyEmail),
    fork(watchAccountRecoveryAddIdentity),
    fork(watchAccountRecoveryVerifyIdentity),
    fork(watchAccountRecoveryResendVerificationCode),
    fork(watchAccountRecoveryResendIdentityVerificationCode),
  ]);
}

export { accountRecoverySaga };
